import { useState } from 'react';
import { useMutation } from '@apollo/client';

import {
    REGISTER_USER_MUTATION,
    RegisterUserInput,
    RegisterUserPayload
} from '../../../../queries/authentication/authentication';

import { UseFormMethods } from 'react-hook-form';

import registrationAndSignInTranslations from 'public/locales/en/registration-and-sign-in.json';
import RegistrationFormData from '../interfaces/RegistrationFormData';
import { getTranslator } from '../../../../shared/i18n/getTranslator';
import { triggerSuccessfulAuthentication } from '../utils/triggerSuccessfulAuthentication';

const t = getTranslator(registrationAndSignInTranslations);

const useRegistration = (
    setError: UseFormMethods<RegistrationFormData>['setError'],
    onSuccess: () => void
) => {
    const [generalError, setGeneralError] = useState(false);

    const [registerMutation] = useMutation<
        { registerUser: RegisterUserPayload },
        { input: RegisterUserInput }
    >(REGISTER_USER_MUTATION, {
        onCompleted({ registerUser: response }) {
            if (response.errorField && response.errorCode) {
                const registerErrorLabel = t(
                    `registerSignInError.${response.errorCode}`
                );
                setError(response.errorField as keyof RegistrationFormData, {
                    type: 'manual',
                    message: registerErrorLabel
                        ? registerErrorLabel
                        : t('serviceError'),
                    shouldFocus: true
                });
                return;
            }
            if (!response.success) {
                setGeneralError(true);
                return;
            }
            triggerSuccessfulAuthentication();
            onSuccess();
        },
        onError() {
            setGeneralError(true);
        }
    });

    const registerUser = (props: RegistrationFormData) => {
        setGeneralError(false);

        const { email, password, situation, consent__insights__24 } = props;

        const input: RegisterUserInput = {
            email,
            password,
            propertyStatus: situation,
            consents: [
                {
                    consentName: 'consent.insights.24',
                    consentGiven: consent__insights__24
                }
            ]
        };

        return registerMutation({
            variables: {
                input
            }
        });
    };

    return {
        registerUser,
        generalError
    };
};

export default useRegistration;
